// src/pages/AccountDeletionRequest.jsx

import React from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Mail,
  ShieldCheck,
  Trash2,
} from "lucide-react";

const AccountDeletionRequest = () => {
  const supportEmail = "support@eblackdog.net";

  const handleContactSupport = () => {
    const subject = encodeURIComponent("Account Deletion Request");
    const body = encodeURIComponent(
      `Hello eBLACKDOG Support,

I would like to request deletion of my eBLACKDOG account and associated personal data.

Please let me know what information is required to process this request.

Name:
Organization:
Registered email address:
Employee/User ID:

Thank you.`
    );

    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="mx-auto max-w-2xl">
        {/* Back Button */}
        <button
          type="button"
          onClick={handleGoBack}
          className="mb-8 flex items-center gap-2 text-gray-600 transition-colors hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
          {/* Warning Header */}
          <div className="bg-red-600 px-8 py-12 text-center text-white">
            <div className="mb-4 flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-red-700/30">
                <AlertTriangle size={48} />
              </div>
            </div>

            <h1 className="mb-3 text-4xl font-bold">Delete Account</h1>

            <p className="text-lg text-red-100">
              Account deletion is permanent and cannot be undone
            </p>
          </div>

          <div className="p-8 md:p-10">
            <div className="space-y-6">
              {/* Organization Managed Account Information */}
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6">
                <h2 className="mb-4 flex items-center gap-2 font-semibold text-blue-900">
                  <ShieldCheck size={22} />
                  Organization-managed account
                </h2>

                <p className="leading-7 text-blue-800">
                  User accounts are managed by the user&apos;s organization,
                  such as their company, school, or institution.
                </p>

                <p className="mt-4 leading-7 text-blue-800">
                  To request account deletion, please contact your
                  organization&apos;s administrator or email our support team
                  at{" "}
                  <a
                    href={`mailto:${supportEmail}`}
                    className="font-semibold underline hover:text-blue-950"
                  >
                    {supportEmail}
                  </a>
                  .
                </p>

                <p className="mt-4 leading-7 text-blue-800">
                  Upon approval by the organization, the account and associated
                  personal data will be deleted in accordance with our data
                  retention policy.
                </p>
              </div>

              {/* Deletion Consequences */}
              <div className="rounded-2xl border border-red-100 bg-red-50 p-6">
                <h2 className="mb-4 flex items-center gap-2 font-semibold text-red-800">
                  <AlertTriangle size={22} />
                  Important consequences
                </h2>

                <ul className="space-y-3 text-red-700">
                  <li className="flex gap-3">
                    <span className="font-semibold">•</span>
                    <span>Your account will be permanently deactivated.</span>
                  </li>

                  <li className="flex gap-3">
                    <span className="font-semibold">•</span>
                    <span>
                      Associated personal data will be deleted according to our
                      retention policy.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="font-semibold">•</span>
                    <span>
                      You will lose access to the application, attendance
                      records, patrol information, and account history.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="font-semibold">•</span>
                    <span>
                      Some records may be retained where required by law,
                      security obligations, contractual requirements, or
                      legitimate organizational record-keeping requirements.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="font-semibold">•</span>
                    <span>
                      Once deletion has been completed, the action cannot be
                      reversed.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Process Information */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <h2 className="mb-4 font-semibold text-gray-900">
                  How to request deletion
                </h2>

                <ol className="space-y-4 text-gray-700">
                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white">
                      1
                    </span>

                    <span>
                      Contact your organization&apos;s administrator and request
                      deletion of your eBLACKDOG account.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white">
                      2
                    </span>

                    <span>
                      Alternatively, email{" "}
                      <a
                        href={`mailto:${supportEmail}`}
                        className="font-semibold text-red-600 underline hover:text-red-700"
                      >
                        {supportEmail}
                      </a>{" "}
                      with your name, organization, registered email address,
                      and employee or user ID.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white">
                      3
                    </span>

                    <span>
                      The request will be verified with your organization before
                      deletion is processed.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-sm font-semibold text-white">
                      4
                    </span>

                    <span>
                      After approval, your account and eligible associated data
                      will be deleted in accordance with the applicable
                      retention policy.
                    </span>
                  </li>
                </ol>
              </div>

              <div className="py-2 text-center">
                <p className="leading-7 text-gray-600">
                  Use the button below to prepare an account deletion request
                  email.
                </p>
              </div>

              {/* Contact Support Button */}
              <button
                type="button"
                onClick={handleContactSupport}
                className="flex w-full items-center justify-center gap-3 rounded-2xl bg-red-600 py-5 text-lg font-semibold text-white shadow-lg shadow-red-500/30 transition-all duration-200 hover:bg-red-700 hover:shadow-xl active:bg-red-800"
              >
                <Trash2 size={24} />
                Request Account Deletion
                <Mail size={21} />
              </button>

              <p className="mt-6 text-center text-sm leading-6 text-gray-500">
                Your email application will open with a prepared deletion
                request. Sending the email does not immediately delete your
                account. The request must first be verified and approved by your
                organization.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-10 text-center text-xs text-gray-500">
          Need assistance? Contact{" "}
          <a
            href={`mailto:${supportEmail}`}
            className="font-medium underline hover:text-gray-700"
          >
            {supportEmail}
          </a>
        </p>
      </div>
    </div>
  );
};

export default AccountDeletionRequest;